import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../Component/Header';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';


function ServicesDetails() {

    const { category_id, name } = useParams();

    // ==========================================
    // CATEGORY / SUBCATEGORY / PRODUCTS
    // ==========================================

    const [subcategories, setSubcategories] = useState([]);
    const [categoryProducts, setCategoryProducts] = useState([]);
    const [productsBySubcategory, setProductsBySubcategory] = useState({});

    const [loadingProducts, setLoadingProducts] = useState(true);
    const [errorProducts, setErrorProducts] = useState(null);


    // ==========================================
    // PACKAGES
    // ==========================================

    const [packages, setPackages] = useState([]);
    const [loadingPackages, setLoadingPackages] = useState(true);
    const [errorPackages, setErrorPackages] = useState(null);


    // ==========================================
    // NOTES
    // ==========================================

    const [note, setNotes] = useState([]);
    const [loadingNotes, setLoadingNotes] = useState(true);
    const [errorNotes, setErrorNotes] = useState(null);


    // ============================================================
    // FETCH CATEGORY + SUBCATEGORIES + PRODUCTS
    // ============================================================

    useEffect(() => {

        const fetchCategoryProducts = async () => {

            try {

                setLoadingProducts(true);
                setErrorProducts(null);

                if (!category_id) {
                    throw new Error('Category ID is missing');
                }


                // ====================================================
                // STEP 1:
                // GET ALL SUBCATEGORIES OF THIS CATEGORY
                // ====================================================

                let subcategoryList = [];

                try {

                    const subcategoryResponse = await fetch(
                        `${process.env.REACT_APP_API_URL}/getsubcategory/category/${category_id}`
                    );

                    if (subcategoryResponse.ok) {

                        const subcategoryResult =
                            await subcategoryResponse.json();

                        subcategoryList =
                            subcategoryResult.data || [];

                    } else if (subcategoryResponse.status !== 404) {

                        throw new Error(
                            'Failed to fetch subcategories'
                        );

                    }

                } catch (subcategoryError) {

                    console.error(
                        'Subcategory API error:',
                        subcategoryError
                    );

                    subcategoryList = [];

                }


                setSubcategories(subcategoryList);


                // ====================================================
                // STEP 2:
                // GET ALL PRODUCTS OF THE CATEGORY
                //
                // IMPORTANT:
                // This API should return ALL products belonging
                // to this category.
                //
                // Then we keep only:
                //
                // subcategory_id === NULL
                //
                // These are direct category products.
                // ====================================================

                let directCategoryProducts = [];

                try {

                    const productResponse = await fetch(
                        `${process.env.REACT_APP_API_URL}/getproducts/category/${category_id}`
                    );

                    if (productResponse.ok) {

                        const productResult =
                            await productResponse.json();

                        const allCategoryProducts =
                            productResult.products || [];


                        // --------------------------------------------
                        // ONLY DIRECT CATEGORY PRODUCTS
                        // --------------------------------------------

                        directCategoryProducts =
                            allCategoryProducts.filter(
                                (product) =>
                                    product.subcategory_id === null ||
                                    product.subcategory_id === undefined
                            );

                    } else if (productResponse.status !== 404) {

                        throw new Error(
                            'Failed to fetch category products'
                        );

                    }

                } catch (categoryProductError) {

                    console.error(
                        'Category product API error:',
                        categoryProductError
                    );

                    directCategoryProducts = [];

                }


                setCategoryProducts(
                    directCategoryProducts
                );


                // ====================================================
                // STEP 3:
                // GET PRODUCTS FOR EACH SUBCATEGORY
                // ====================================================

                const productResults = {};


                await Promise.all(

                    subcategoryList.map(
                        async (subcategory) => {

                            try {

                                const response = await fetch(
                                    `${process.env.REACT_APP_API_URL}/getproducts/subcategory/${subcategory.subcategory_id}`
                                );


                                if (response.status === 404) {

                                    productResults[
                                        subcategory.subcategory_id
                                    ] = [];

                                    return;
                                }


                                if (!response.ok) {

                                    throw new Error(
                                        `Failed to fetch products for ${subcategory.subcategory}`
                                    );

                                }


                                const result =
                                    await response.json();


                                productResults[
                                    subcategory.subcategory_id
                                ] =
                                    result.products || [];


                            } catch (error) {

                                console.error(
                                    `Error fetching products for ${subcategory.subcategory}:`,
                                    error
                                );


                                productResults[
                                    subcategory.subcategory_id
                                ] = [];

                            }

                        }
                    )

                );


                setProductsBySubcategory(
                    productResults
                );


                setLoadingProducts(false);


            } catch (error) {

                console.error(
                    'Error fetching category details:',
                    error
                );

                setErrorProducts(
                    error.message
                );

                setLoadingProducts(false);

            }

        };


        fetchCategoryProducts();

    }, [category_id]);


    // ============================================================
    // FETCH PACKAGES
    // ============================================================

    useEffect(() => {

        const fetchPackages = async () => {

            try {

                setLoadingPackages(true);

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/getpackage/category/${category_id}`
                );


                if (response.status === 404) {

                    setPackages([]);
                    setErrorPackages(null);
                    setLoadingPackages(false);

                    return;
                }


                if (!response.ok) {

                    throw new Error(
                        'No packages found'
                    );

                }


                const result =
                    await response.json();


                setPackages(
                    result.packages || []
                );

                setErrorPackages(null);

                setLoadingPackages(false);


            } catch (error) {

                setErrorPackages(
                    error.message
                );

                setLoadingPackages(false);

            }

        };


        fetchPackages();

    }, [category_id]);


    // ============================================================
    // FETCH NOTES
    // ============================================================

    useEffect(() => {

        const fetchNotes = async () => {

            try {

                setLoadingNotes(true);

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/getnote/category/${category_id}`
                );


                if (response.status === 404) {

                    setNotes([]);
                    setErrorNotes(null);
                    setLoadingNotes(false);

                    return;
                }


                if (!response.ok) {

                    throw new Error(
                        'No Notes found'
                    );

                }


                const result =
                    await response.json();


                setNotes(
                    result.notes || []
                );

                setErrorNotes(null);

                setLoadingNotes(false);


            } catch (error) {

                setErrorNotes(
                    error.message
                );

                setLoadingNotes(false);

            }

        };


        fetchNotes();

    }, [category_id]);


    // ============================================================
    // PRODUCT SLIDER
    // ============================================================

    const ProductSlider = ({ products }) => {

        if (!products || products.length === 0) {

            return (
                <div
                    className="text-center text-muted"
                    style={{
                        padding: '20px 0'
                    }}
                >
                    No products found
                </div>
            );

        }


        return (

            <Swiper

                modules={[
                    Navigation,
                    Autoplay
                ]}

                spaceBetween={20}

                navigation

                autoplay={{
                    delay: 2000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                }}

                speed={1000}

                breakpoints={{

                    0: {
                        slidesPerView: 1,
                        spaceBetween: 10
                    },

                    576: {
                        slidesPerView: 2,
                        spaceBetween: 15
                    },

                    768: {
                        slidesPerView: 3,
                        spaceBetween: 20
                    },

                    992: {
                        slidesPerView: 4,
                        spaceBetween: 30
                    }

                }}

                style={{
                    paddingBottom: '40px'
                }}

            >

                {products.map((product) => (

                    <SwiperSlide
                        key={product.product_id}
                    >

                        <div
                            className="service-item d-flex flex-column bg-light"
                            style={{
                                width: '100%',
                                borderRadius: '8px',
                                border: '2px solid #ddd',
                                overflow: 'hidden'
                            }}
                        >

                            <img
                                className="img-fluid"
                                src={product.product_image}
                                alt={
                                    product.product_name ||
                                    'Product'
                                }
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    transition:
                                        'transform 0.3s ease',
                                    display: 'block'
                                }}

                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform =
                                        'scale(1.05)';
                                }}

                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform =
                                        'scale(1)';
                                }}
                            />

                        </div>

                    </SwiperSlide>

                ))}

            </Swiper>

        );

    };


    // ============================================================
    // RETURN UI
    // ============================================================

    return (

        <div>

            <Header />


            {/* ==================================================
                CATEGORY TITLE
            ================================================== */}

            <h3
                className="text-center"
                style={{
                    color: '#EAA636',
                    marginTop: '150px',
                    fontSize: '3rem'
                }}
            >
                {name}
            </h3>


            <div className="container p-5">


                {/* ==================================================
                    PRODUCTS
                ================================================== */}

                {loadingProducts ? (

                    <div className="text-center">
                        Loading products...
                    </div>

                ) : errorProducts ? (

                    <div className="text-center text-danger">
                        {errorProducts}
                    </div>

                ) : (

                    <div>


                        {/* ==================================================
                            DIRECT CATEGORY PRODUCTS
                            
                            Example:
                            
                            TEST
                            └── Product directly under TEST
                            
                            These have:
                            subcategory_id = NULL
                        ================================================== */}

                        {categoryProducts.length > 0 && (

                            <section
                                style={{
                                    marginBottom: '60px'
                                }}
                            >

                                <ProductSlider
                                    products={
                                        categoryProducts
                                    }
                                />

                            </section>

                        )}


                        {/* ==================================================
                            SUBCATEGORY PRODUCTS
                            
                            Example:
                            
                            TEST
                            │
                            ├── SUbtest
                            │      └── Products
                            │
                            └── Subtest2
                                   └── Products
                        ================================================== */}

                        {subcategories.map(
                            (subcategory) => {

                                const products =
                                    productsBySubcategory[
                                        subcategory.subcategory_id
                                    ] || [];


                                return (

                                    <section
                                        key={
                                            subcategory.subcategory_id
                                        }

                                        style={{
                                            marginBottom:
                                                '60px'
                                        }}
                                    >


                                        {/* SUBCATEGORY NAME */}

                                        <h3
                                            className="text-center"
                                            style={{
                                                color: '#EAA636',
                                                marginTop: '30px',
                                                marginBottom: '30px',
                                                fontSize: '2.5rem'
                                            }}
                                        >
                                            {
                                                subcategory.subcategory
                                            }
                                        </h3>


                                        {/* SUBCATEGORY PRODUCTS */}

                                        <ProductSlider
                                            products={
                                                products
                                            }
                                        />

                                    </section>

                                );

                            }
                        )}


                        {/* ==================================================
                            NOTHING FOUND
                        ================================================== */}

                        {categoryProducts.length === 0 &&
                            subcategories.length === 0 && (

                                <div
                                    className="text-center text-muted"
                                    style={{
                                        padding:
                                            '30px 0'
                                    }}
                                >
                                    No products found
                                </div>

                            )}

                    </div>

                )}


                {/* ==================================================
                    PACKAGES
                ================================================== */}

                <h3
                    className="text-center"
                    style={{
                        color: '#EAA636',
                        marginTop: '40px',
                        marginBottom: '20px',
                        fontSize: '3rem'
                    }}
                >
                    Pricing Packages
                </h3>


                {loadingPackages ? (

                    <div className="text-center mt-5">
                        Loading packages...
                    </div>

                ) : errorPackages ? (

                    <div className="text-center text-danger mt-5">
                        {errorPackages}
                    </div>

                ) : packages.length === 0 ? (

                    <div className="text-center mt-5">
                        No packages found
                    </div>

                ) : (

                    <div className="row">

                        {packages.map((pkg) => (

                            <div
                                key={pkg.packages_id}
                                className="col-12 col-md-6 col-lg-3 mb-4"
                            >

                                <div
                                    className="package-card h-100"
                                    style={{
                                        backgroundColor: '#f6f6f6',
                                        borderRadius: '12px',
                                        border: '1px solid #ccc',
                                        padding: '24px',
                                        boxShadow:
                                            '0 2px 8px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent:
                                            'space-between'
                                    }}
                                >

                                    <div>

                                        <h4 className="mb-1">
                                            {pkg.title}
                                        </h4>


                                        <h2 className="text-dark">

                                            {pkg.price &&
                                            pkg.price > 0
                                                ? `₹${Number(
                                                    pkg.price
                                                ).toLocaleString()} / Package`
                                                : ''
                                            }

                                        </h2>


                                        <button
                                            className="btn btn-outline-dark mb-3"

                                            onClick={() => {

                                                const message =
                                                    `Hi, I'm interested in the "${pkg.title}" package.\n` +
                                                    `Price: ${
                                                        pkg.price
                                                            ? `₹${Number(
                                                                pkg.price
                                                            ).toLocaleString()}`
                                                            : 'Contact for price'
                                                    }\n` +
                                                    `Please share more details.`;

                                                window.open(
                                                    `https://wa.me/+919265197287?text=${encodeURIComponent(
                                                        message
                                                    )}`,
                                                    '_blank'
                                                );

                                            }}
                                        >
                                            Contact US
                                        </button>


                                        <h6 className="fw-bold text-dark">
                                            What's included?
                                        </h6>


                                        <div>

                                            {(pkg.description || '')
                                                .split('\n')
                                                .map(
                                                    (
                                                        line,
                                                        idx
                                                    ) => (

                                                        <p
                                                            key={
                                                                idx
                                                            }
                                                            className="mb-1"
                                                        >
                                                            ✓{' '}
                                                            {line.replace(
                                                                '* ',
                                                                ''
                                                            )}
                                                        </p>

                                                    )
                                                )}

                                        </div>

                                    </div>

                                </div>

                            </div>

                        ))}

                    </div>

                )}


                {/* ==================================================
                    NOTES
                ================================================== */}

                <h3
                    className="text-center"
                    style={{
                        color: '#EAA636',
                        marginTop: '40px',
                        marginBottom: '20px',
                        fontSize: '3rem'
                    }}
                >
                    Points to Note
                </h3>


                {loadingNotes ? (

                    <div className="text-center mt-5">
                        Loading Notes...
                    </div>

                ) : errorNotes ? (

                    <div className="text-center text-danger mt-5">
                        {errorNotes}
                    </div>

                ) : note.length === 0 ? (

                    <div className="text-center mt-5">
                        No Notes found
                    </div>

                ) : (

                    <div className="row justify-content-center">

                        <div className="col-12 col-md-10 col-lg-12">

                            <div
                                className="notes-card"
                                style={{
                                    backgroundColor:
                                        '#f6f6f6',
                                    borderRadius:
                                        '12px',
                                    border:
                                        '1px solid #ccc',
                                    padding: '24px',
                                    boxShadow:
                                        '0 2px 8px rgba(0,0,0,0.1)'
                                }}
                            >

                                {note.map(
                                    (n, idx) => (

                                        <div
                                            key={
                                                n.notes_id
                                            }
                                        >

                                            {(
                                                n.note_text ||
                                                ''
                                            )
                                                .split('\n')
                                                .map(
                                                    (
                                                        line,
                                                        i
                                                    ) => (

                                                        <p
                                                            key={
                                                                i
                                                            }
                                                            className="mb-3"
                                                            style={{
                                                                lineHeight:
                                                                    '1.6'
                                                            }}
                                                        >

                                                            <strong
                                                                style={{
                                                                    marginRight:
                                                                        '8px'
                                                                }}
                                                            >
                                                                •
                                                            </strong>

                                                            {line.replace(
                                                                '* ',
                                                                ''
                                                            )}

                                                        </p>

                                                    )
                                                )}


                                            {idx <
                                                note.length -
                                                    1 && (
                                                <hr />
                                            )}

                                        </div>

                                    )
                                )}

                            </div>

                        </div>

                    </div>

                )}

            </div>

        </div>

    );

}

export default ServicesDetails;